import { memo, startTransition, useEffect, useRef, useState } from 'react';
import { SnoflakeCanvas } from './styled';
import {
  PaintSnowflakeOnCanvasArgs,
  SnowflakeSection,
  paintSnowflakeOnCanvas,
} from './utils';
import useResizeObserver from 'use-resize-observer';
import Labels from '../Labels';
import { Box, useTheme } from '@simplywallst/ui-core';
import { isEqual } from 'lodash';

const MemoLabels = memo(Labels);

type Props = {
  dark?: boolean;
  size?: number;
  light?: boolean;
  points: number[];
  disabled?: boolean;
  disableLabel?: boolean;
  highlightSection?: SnowflakeSection;
};

const SnowflakeCanvas = ({
  points,
  size,
  disabled,
  disableLabel,
  highlightSection,
  dark,
  light,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [containerSize, setCanvasSize] = useState<number | null>(null);
  const [paintedArgs, setPaintedArgs] =
    useState<PaintSnowflakeOnCanvasArgs | null>(null);

  const { ref } = useResizeObserver({
    round: Math.floor,
    onResize: ({ width }) => {
      startTransition(() => {
        // Avoids unnecessary repainting by setting the desired resolution to the nearest 100 (rounding up)
        width && setCanvasSize(Math.ceil(width / 100) * 100);
      });
    },
  });

  const theme = useTheme();

  const targetCanvasSize = (size || containerSize || 0) * 2;

  const targetPaintArgs = {
    size: targetCanvasSize,
    dark,
    light,
    disabled,
    disableLabel,
    highlightedSection: highlightSection,
    points,
  };

  // Only paint if Snowflake parameters changed
  const requiresRepaint =
    !isEqual(targetPaintArgs, paintedArgs) && targetPaintArgs.size;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!requiresRepaint) return;

    paintSnowflakeOnCanvas(
      {
        canvas,
        theme,
      },
      targetPaintArgs
    );

    setPaintedArgs(targetPaintArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresRepaint]);

  return (
    <Box ref={ref} minWidth={size ? `${size}px` : '0'}>
      <SnoflakeCanvas
        ref={canvasRef}
        width={targetCanvasSize}
        height={targetCanvasSize}
      />
      {!disableLabel && <MemoLabels highlightSection={highlightSection} />}
    </Box>
  );
};

export default SnowflakeCanvas;
